import React, { useRef, useEffect, useState } from 'react';
import Webcam from 'react-webcam';
import { BrowserMultiFormatReader } from '@zxing/browser';
import './BarcodeScannerModal.css';

/**
 * BarcodeScannerModal
 * A modal for scanning barcodes using the device camera.
 * Props:
 *   show: boolean - whether the modal is visible
 *   onDetected: function(barcode) - called with barcode string on successful scan
 *   onClose: function() - called to close the modal
 *   scanLoading, scanError: feedback states (optional)
 *   scannedProductData: object - product data after successful scan
 *   showScannedProduct: boolean - whether to show product details
 *   onConfirmProduct: function() - called when user confirms the product
 *   onRetryScanning: function() - called when user wants to scan again
 */
const BarcodeScannerModal = ({ 
  show, 
  onDetected, 
  onClose, 
  scanLoading, 
  scanError,
  scannedProductData,
  showScannedProduct,
  onConfirmProduct,
  onRetryScanning
}) => {
  const webcamRef = useRef(null);
  const codeReader = useRef(null);
  const animationRef = useRef(null);
  const [isScanning, setIsScanning] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  useEffect(() => {
    if (!show) {
      setIsScanning(false);
      setShowSuccess(false);
      return;
    }
    
    codeReader.current = new BrowserMultiFormatReader();
    let stopped = false;
    
    // Start scanning animation after a short delay to ensure video is ready
    const startScanningTimer = setTimeout(() => {
      if (!stopped) setIsScanning(true);
    }, 500);

    const scan = async () => {
      if (!webcamRef.current) return;
      const video = webcamRef.current.video;
      if (!video || video.readyState !== 4) {
        animationRef.current = requestAnimationFrame(scan);
        return;
      }
      try {
        const result = await codeReader.current.decodeOnceFromVideoElement(video);
        if (!stopped && result && result.text) {
          // Show success animation
          setShowSuccess(true);
          setIsScanning(false);
          
          // Call onDetected after a brief delay to show success animation
          setTimeout(() => {
            onDetected(result.text);
          }, 500);
        }
      } catch (err) {
        // Ignore not found errors, only log real errors
      }
      if (!stopped) animationRef.current = requestAnimationFrame(scan);
    };
    
    scan();
    
    return () => {
      stopped = true;
      clearTimeout(startScanningTimer);
      setIsScanning(false);
      setShowSuccess(false);
      if (codeReader.current && typeof codeReader.current.reset === 'function') {
        codeReader.current.reset();
      }
      if (animationRef.current) cancelAnimationFrame(animationRef.current);
    };
    // eslint-disable-next-line
  }, [show, onDetected]);
  if (!show) return null;
  return (
    <div className="modal d-block" tabIndex="-1" style={{ background: 'rgba(0,0,0,0.5)' }}>
      <div className="modal-dialog modal-dialog-centered barcode-modal-enter">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">Scan Barcode</h5>
            <button type="button" className="btn-close" onClick={onClose}></button>
          </div>          <div className="modal-body text-center">
            {!showScannedProduct ? (
              <>
                {/* Barcode Scanning View */}
                <div className={`barcode-scanner-container ${isScanning ? 'scanner-active' : ''}`}>
                  <Webcam
                    audio={false}
                    ref={webcamRef}
                    screenshotFormat="image/png"
                    videoConstraints={{ facingMode: 'environment' }}
                    style={{ width: '100%', borderRadius: 12, marginBottom: 8 }}
                  />
                  
                  {/* Scanning overlay with animations */}
                  {isScanning && (
                    <div className="scanning-overlay">
                      {/* Animated scanning line */}
                      <div className="scanning-line"></div>
                      
                      {/* Corner brackets */}
                      <div className="scanning-corners">
                        <div className="corner corner-tl"></div>
                        <div className="corner corner-tr"></div>
                        <div className="corner corner-bl"></div>
                        <div className="corner corner-br"></div>
                      </div>
                      
                      {/* Loading spinner */}
                      {scanLoading && <div className="scanning-loader"></div>}
                    </div>
                  )}
                  
                  {/* Success animation */}
                  {showSuccess && (
                    <div className="scan-success">
                      <div className="success-checkmark">✓</div>
                    </div>
                  )}
                </div>
                
                {scanLoading && <div className="alert alert-info mt-2">Scanning barcode...</div>}
                {scanError && <div className="alert alert-danger mt-2">{scanError}</div>}
                <div className={`text-muted mt-2 ${isScanning ? 'scanning-instructions' : ''}`}>
                  Point your camera at a barcode
                </div>
              </>
            ) : (
              <>
                {/* Scanned Product Details View */}
                <div className="scanned-product-details">
                  <div className="success-icon mb-3">
                    <div className="success-checkmark-large">✓</div>
                  </div>
                  
                  <h4 className="product-name mb-3">{scannedProductData?.name}</h4>
                  
                  {scannedProductData?.description && (
                    <p className="product-description text-muted mb-3">
                      {scannedProductData.description}
                    </p>
                  )}
                  
                  <div className="nutrition-info">
                    <div className="row">
                      <div className="col-6 col-md-3 mb-2">
                        <div className="nutrition-item">
                          <div className="nutrition-value">{scannedProductData?.calories || 0}</div>
                          <div className="nutrition-label">Calories</div>
                        </div>
                      </div>
                      <div className="col-6 col-md-3 mb-2">
                        <div className="nutrition-item">
                          <div className="nutrition-value">{scannedProductData?.protein || 0}g</div>
                          <div className="nutrition-label">Protein</div>
                        </div>
                      </div>
                      <div className="col-6 col-md-3 mb-2">
                        <div className="nutrition-item">
                          <div className="nutrition-value">{scannedProductData?.carbs || 0}g</div>
                          <div className="nutrition-label">Carbs</div>
                        </div>
                      </div>
                      <div className="col-6 col-md-3 mb-2">
                        <div className="nutrition-item">
                          <div className="nutrition-value">{scannedProductData?.fat || 0}g</div>
                          <div className="nutrition-label">Fat</div>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  {scannedProductData?.serving_size && (
                    <div className="serving-size mt-3">
                      <small className="text-muted">Serving size: {scannedProductData.serving_size}</small>
                    </div>
                  )}
                  
                  <div className="action-buttons mt-4">
                    <button 
                      type="button" 
                      className="btn btn-success me-2"
                      onClick={onConfirmProduct}
                    >
                      Add to Log
                    </button>
                    <button 
                      type="button" 
                      className="btn btn-outline-secondary"
                      onClick={onRetryScanning}
                    >
                      Scan Again
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default BarcodeScannerModal;
